import React, { useEffect, useRef, useState } from 'react';
import { Shield, FileText, Clock, Lock, DollarSign, RefreshCw } from 'lucide-react';

const benefits = [
  {
    icon: Shield,
    title: 'Avoid Probate Completely',
    description: 'Your beneficiaries immediately gain access to the property without going through the lengthy and expensive probate court process.',
  },
  {
    icon: Lock,
    title: 'Retain Full Control',
    description: 'Sell, mortgage, or even cancel the deed entirely during your lifetime — without needing permission from your beneficiaries.',
  },
  {
    icon: FileText,
    title: 'Preserve Homestead Protection',
    description: 'Properly drafted Lady Bird Deeds typically preserve Florida homestead protections and property tax exemptions.',
  },
  {
    icon: Clock,
    title: 'Simple Transfer Process',
    description: 'After passing, heirs simply record the death certificate to update property records — no court involvement needed.',
  },
  {
    icon: RefreshCw,
    title: 'Completely Revocable',
    description: 'Change your mind anytime. Modify beneficiaries or cancel the deed entirely by recording a new document.',
  },
  {
    icon: DollarSign,
    title: 'Cost-Effective Solution',
    description: 'A simpler, more affordable alternative to setting up a trust, with typical costs ranging from $800-$1,000.',
  },
];

const BenefitsSection = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.benefit-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                setVisibleCards(prev => [...prev, index]);
              }, index * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits" className="relative py-24 lg:py-32 bg-[#0a0a0a]" ref={sectionRef}>
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 divider-elegant" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#c9a87c] mb-4 block">
            Why Choose a Lady Bird Deed
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#faf9f7] mb-6">
            The Smart Way to <span className="text-gradient-gold">Protect Your Legacy</span>
          </h2>
          <p className="font-sans text-lg text-[#faf9f7]/60 max-w-2xl mx-auto">
            A Lady Bird Deed offers Florida homeowners a unique combination of control, 
            flexibility, and peace of mind that other estate planning tools simply can't match.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`benefit-card group relative p-8 bg-gradient-to-br from-[#141414] to-[#0f0f0f] border border-[#c9a87c]/10 rounded-lg card-hover transition-all duration-500 ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Subtle glow on hover */}
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-[#c9a87c]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                {/* Icon */}
                <div className="w-12 h-12 rounded-lg bg-[#c9a87c]/10 flex items-center justify-center mb-6 group-hover:bg-[#c9a87c]/20 transition-colors duration-300">
                  <benefit.icon className="w-6 h-6 text-[#c9a87c]" />
                </div>
                
                {/* Title */}
                <h3 className="font-serif text-xl text-[#faf9f7] mb-3 group-hover:text-[#c9a87c] transition-colors duration-300">
                  {benefit.title}
                </h3>
                
                {/* Description */}
                <p className="font-sans text-sm text-[#faf9f7]/60 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 font-sans text-[#c9a87c] hover:text-[#d4b896] transition-colors duration-300 link-underline"
          >
            <span>Discover if a Lady Bird Deed is right for you</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
